/* eslint-disable no-mixed-operators */
import React, { useMemo, useState, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { FloorPlanPackageName, PerFloorPackages, FloorPlanPackage } from 'auto-design-common';
import { useLocation } from 'react-router-dom';
import QueryString from 'query-string';
import config from 'configuration';
import ReactLoading from 'components/Common/ReactLoading';
import { useAppContext } from 'components/AppContext';
import { post } from 'utils/request';
import { formatCurrency } from 'utils/currency';
import useTranslation from '../../hooks/useTranslation';
import styles from './MobileCheckOut.module.scss';

export const getPriceKey = (packageId) => `floor_plan_${packageId}_usd_price`;

function MobileCheckOutContent() {
  const { useTranslationOrigin } = useTranslation();
  const { t: tCheckout } = useTranslationOrigin('translation', { keyPrefix: 'mobile.checkout' });
  const { t: tCommon } = useTranslationOrigin('translation', { keyPrefix: 'common' });

  const [{ isPending }] = usePayPalScriptReducer();
  const { configs, user } = useAppContext();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Parse query parameters
  const { floorPlanId, packages: packagesFromQuery, floors: floorsFromQuery } = QueryString.parse(location.search);

  const packages = useMemo(() => packagesFromQuery?.split(',') || [], [packagesFromQuery]);

  const floors = Number(floorsFromQuery) || 1;

  const isFreeRoomPlan = useMemo(() => (
    user?.freeRoomPlans > 0 && packages.length === 1 && packages[0] === FloorPlanPackage.ROOM_PLAN
  ), [user, packages]);

  const totalPrice = useMemo(() => packages.reduce((acc, packageId) => {
    if (isFreeRoomPlan && packageId === FloorPlanPackage.ROOM_PLAN) {
      return acc;
    }
    const unit = configs[getPriceKey(packageId)] / 100;
    const quantity = PerFloorPackages.includes(packageId) ? floors : 1;
    return acc + unit * quantity;
  }, 0), [packages, configs, floors, isFreeRoomPlan]);

  // Handle successful payment
  // Send message to React Native WebView
  const sendMessageToWebView = useCallback((type, data) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type,
        ...data,
      }));
    }
  }, []);

  // Create order function for PayPal
  const createOrder = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await post(`/me/floor-plans/${floorPlanId}/mobile-packages`, {
        packages,
      });

      setLoading(false);

      // Notify WebView about order creation
      sendMessageToWebView('ORDER_CREATED', {
        orderId: response.paypalOrderId,
        floorPlanId,
        packages,
      });

      return response.paypalOrderId;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create order';
      setLoading(false);
      setError(errorMessage);

      // Notify WebView about order creation error
      sendMessageToWebView('ORDER_ERROR', {
        error: errorMessage,
        floorPlanId,
        packages,
      });

      throw err;
    }
  };

  const onApprove = async (data) => {
    try {
      setLoading(true);
      setError(null);

      await post(`/me/floor-plans/${floorPlanId}/capture-mobile-payment`, {
        paypalOrderId: data.orderID,
      });

      // Send success message to WebView
      sendMessageToWebView('PAYMENT_SUCCESS', {
        orderId: data.orderID,
        floorPlanId,
        packages,
      });
    } catch (err) {
      setLoading(false);
      const errorMessage = err.message || 'Failed to process payment';
      setError(errorMessage);

      // Send error message to WebView
      sendMessageToWebView('PAYMENT_ERROR', {
        error: errorMessage,
        orderId: data?.orderID,
        floorPlanId,
      });
    }
  };

  if (!floorPlanId) {
    return (
      <div className={styles.mobileCheckoutError}>
        <h2>{tCommon('error')}</h2>
        <p>{tCheckout('missingFloorPlanId')}</p>
      </div>
    );
  }

  return (
    <div className={styles.mobileCheckoutContainer}>
      <h1>{tCheckout('title')}</h1>
      <div className={styles.orderSummary}>{tCheckout('orderSumary')}</div>
      <div className={styles.packages}>
        <table>
          <thead>
            <tr>
              <th>{tCheckout('package')}</th>
              <th>{tCheckout('quantity')}</th>
              <th>{tCheckout('unitPrice')}</th>
              <th>{tCheckout('price')}</th>
            </tr>
          </thead>
          <tbody>
            {packages.map(packageId => (
              <tr key={packageId}>
                <td>{FloorPlanPackageName[packageId]}</td>
                <td>{PerFloorPackages.includes(packageId) ? floors : 1}</td>
                <td>
                  {isFreeRoomPlan && packageId === FloorPlanPackage.ROOM_PLAN ? (
                    <span className={styles.freePrice}>{tCheckout('free')}</span>
                  ) : (
                    formatCurrency(configs[getPriceKey(packageId)] / 100, configs.currency_format)
                  )}
                </td>
                <td>
                  {isFreeRoomPlan && packageId === FloorPlanPackage.ROOM_PLAN ? (
                    <span className={styles.freePrice}>{tCheckout('free')}</span>
                  ) : (
                    formatCurrency(configs[getPriceKey(packageId)] / 100 * (PerFloorPackages.includes(packageId) ? floors : 1), configs.currency_format)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

      </div>
      <div className={styles.total}>
        <div>{tCheckout('totalPrice')}</div>
        <div>
          {formatCurrency(totalPrice, configs.currency_format)}
        </div>
      </div>
      {error && (
        <div className={styles.mobileCheckoutError}>
          <p>{error}</p>
        </div>
      )}

      <div className={styles.paypalButtonContainer}>
        {isFreeRoomPlan ? (
          <Button
            style={{
              width: '100%',
            }}
            disabled={isPending}
            className={styles.confirmButton}
            onClick={async () => {
              try {
                const orderId = await createOrder();
                await onApprove({ orderID: orderId });
              } catch (err) {
                // Error is already handled in createOrder/onApprove
              }
            }}
          >
            {tCommon('confirm')}
          </Button>
        ) : (
          <PayPalButtons
            style={{ layout: 'vertical' }}
            createOrder={createOrder}
            onApprove={onApprove}
          />
        )}
      </div>

      {!isFreeRoomPlan && (isPending || loading) && (
        <div className={styles.fullDivLoading}>
          <ReactLoading />
        </div>
      )}
    </div>
  );
}

export default function MobileCheckOut() {
  return (
    <PayPalScriptProvider options={{ 'client-id': config.paypalClientId }}>
      <MobileCheckOutContent />
    </PayPalScriptProvider>
  );
}
